import { getApiErrorCode } from '@/lib/api-errors';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import type { Product, ProductPosition, ProductSize } from '@/api/products';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  LoadingState,
} from '@/components/ui';
import { useProduct } from '@/hooks/useProducts';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { formatPrice } from '@/lib/utils';

/** Fallback fill for a colour without a hex code. */
const UNKNOWN_COLOR = 'transparent';

/**
 * Which column a long description came from. The catalogue stores it only
 * as German (`description`) and English (`descriptionEn`) HTML and the API
 * hands both through untranslated, so the dialog says which one it shows
 * whenever that is not the shop language.
 */
export type DescriptionSource = 'de' | 'en';

export interface ResolvedDescription {
  text: string;
  source: DescriptionSource;
}

/**
 * The long description for a shop language plus the language it is really
 * in. The sync copies German into `descriptionEn` for products without an
 * English text, so an English column equal to the German one counts as
 * German — that is the only signal the response carries.
 */
export function resolveDescription(
  product: Pick<Product, 'description' | 'descriptionEn'>,
  language: string
): ResolvedDescription | null {
  const de = product.description?.trim() || null;
  const en = product.descriptionEn?.trim() || null;
  const englishIsOwnText = en !== null && en !== de;

  if (language === 'de') {
    if (de) return { text: de, source: 'de' };
    return en ? { text: en, source: englishIsOwnText ? 'en' : 'de' } : null;
  }
  if (englishIsOwnText && en) return { text: en, source: 'en' };
  if (de) return { text: de, source: 'de' };
  return en ? { text: en, source: 'en' } : null;
}

const HTML_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

/**
 * The catalogue description as plain lines. It arrives as a small HTML
 * list; nothing in the app renders supplier HTML, so the markup is turned
 * into one line per list item and every tag is dropped rather than trusted.
 */
export function toDescriptionLines(html: string): string[] {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(li|p|div|tr|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/gi, (entity) => {
      return HTML_ENTITIES[entity.toLowerCase()] ?? entity;
    })
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** Every size of the product, in catalogue order, without duplicates. */
function sizesOf(product: Product): ProductSize[] {
  const seen = new Map<string, ProductSize>();
  for (const variant of product.variants ?? []) {
    if (variant.productSize && !seen.has(variant.productSize.id)) {
      seen.set(variant.productSize.id, variant.productSize);
    }
  }
  return [...seen.values()].sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Print area of a position as "330 × 450 mm", when both sides are known. */
function printAreaSize(
  position: ProductPosition
): { w: number; h: number } | null {
  const area = position.printArea;
  if (!area) return null;
  const w = area.maxWidthMm;
  const h = area.maxHeightMm;
  return w && h ? { w: Math.round(w), h: Math.round(h) } : null;
}

/** One labelled fact; renders nothing without a value. */
function Fact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="dc-type-label text-mute">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}

/** A titled block of the dialog; renders nothing while it would be empty. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h4 className="dc-h3 text-sm text-ink">{title}</h4>
      {children}
    </section>
  );
}

function ProductInfoBody({ product }: { product: Product }) {
  const { t, i18n } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);

  const prefix = 'seller.bulk.reference.products.info';
  const imageUrl = proxyStorageUrl(
    product.mockupImageUrl ?? product.thumbnailImageUrl
  );
  const description = resolveDescription(product, i18n.language);
  const descriptionLines = description
    ? toDescriptionLines(description.text)
    : [];
  const languageNote =
    description && description.source !== i18n.language
      ? t(`${prefix}.descriptionOnlyIn.${description.source}`)
      : null;
  const sizes = sizesOf(product);
  const colors = product.colors ?? [];
  const positions = product.positions ?? [];
  const recommended = Number(product.recommendedSalesPrice);
  const summary = product.shortDescription?.trim() || null;
  const oneLine = product.oneLineDescription?.trim() || null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start gap-4">
        <span className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-control bg-paper ring-1 ring-line-2">
          {imageUrl && !imageFailed ? (
            <img
              src={imageUrl}
              alt={t(`${prefix}.imageAlt`, { name: product.name })}
              loading="lazy"
              onError={() => setImageFailed(true)}
              className="h-full w-full object-contain"
            />
          ) : (
            <ImageOff className="h-5 w-5 text-mute" aria-hidden="true" />
          )}
        </span>
        <dl className="flex min-w-0 flex-1 basis-[12rem] flex-wrap gap-x-6 gap-y-3">
          <Fact
            label={t(`${prefix}.manufacturer`)}
            value={product.manufacturer?.name ?? null}
          />
          <Fact
            label={t(`${prefix}.category`)}
            value={product.category?.name ?? null}
          />
          <Fact
            label={t(`${prefix}.recommendedPrice`)}
            value={
              Number.isFinite(recommended) && recommended > 0
                ? formatPrice(recommended)
                : null
            }
          />
        </dl>
      </div>

      {summary && <p className="text-sm text-ink-2">{summary}</p>}
      {oneLine && <p className="-mt-3 text-[12.5px] text-mute">{oneLine}</p>}

      {product.material && (
        <Section title={t(`${prefix}.material`)}>
          <p className="text-sm text-ink-2">{product.material}</p>
        </Section>
      )}

      {descriptionLines.length > 0 && (
        <Section title={t(`${prefix}.description`)}>
          {descriptionLines.length > 1 ? (
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-ink-2">
              {descriptionLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-2">{descriptionLines[0]}</p>
          )}
          {languageNote && (
            <p className="text-[12px] text-mute">{languageNote}</p>
          )}
        </Section>
      )}

      {colors.length > 0 && (
        <Section title={t(`${prefix}.colors`)}>
          <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
            {colors.map((color) => (
              <li
                key={color.id}
                className="flex items-center gap-1.5 text-[12.5px] text-ink-2"
              >
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: color.hexCode || UNKNOWN_COLOR }}
                  className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-inset ring-line"
                />
                {color.name}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {sizes.length > 0 && (
        <Section title={t(`${prefix}.sizes`)}>
          <ul className="flex flex-wrap gap-1.5">
            {sizes.map((size) => (
              <li
                key={size.id}
                className="rounded-pill bg-paper-3 px-2 py-0.5 font-mono text-[12px] text-ink-2"
              >
                {size.name}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {positions.length > 0 && (
        <Section title={t(`${prefix}.positions`)}>
          <ul className="flex flex-col gap-1 text-sm text-ink-2">
            {positions.map((position) => {
              const size = printAreaSize(position);
              return (
                <li key={position.id} className="flex flex-wrap gap-x-2">
                  <span className="text-ink">{position.name}</span>
                  {size && (
                    <span className="font-mono text-[12px] text-mute">
                      {t(`${prefix}.positionSize`, {
                        width: size.w,
                        height: size.h,
                      })}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>
      )}
    </div>
  );
}

export interface ProductInfoDialogProps {
  /** Product to describe; `null` keeps the dialog closed and the query off. */
  productId: string | null;
  /** Row name, shown as the title until the request answers. */
  productName: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Product information of one row of the reference product panel: image,
 * manufacturer, category, material, description, colours, sizes and print
 * areas, in the shop language the API translates the catalogue names into.
 * Supplier purchase prices never leave the API and are not shown here; the
 * recommended sales price is. The request runs only while the dialog is
 * open and React Query keeps the answer, so reopening is instant.
 */
export function ProductInfoDialog({
  productId,
  productName,
  onOpenChange,
}: ProductInfoDialogProps) {
  const { t } = useTranslation();
  const { data, isPending, isError, error, refetch } = useProduct(
    productId ?? undefined
  );
  // Not set up for designing (409): no retry helps, another product does.
  const uncalibrated = getApiErrorCode(error) === 'product_uncalibrated';
  const prefix = 'seller.bulk.reference.products.info';

  return (
    <Dialog open={productId !== null} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('common.close')}
        className="max-h-[85vh] max-w-2xl overflow-y-auto"
      >
        <DialogTitle>{data?.name ?? productName}</DialogTitle>
        <DialogDescription>{t(`${prefix}.intro`)}</DialogDescription>

        {isPending && <LoadingState label={t(`${prefix}.loading`)} />}

        {isError && uncalibrated && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p role="alert" className="text-sm text-danger">
              {t('errors.product_uncalibrated')}
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('creator.products.chooseOtherProduct')}
            </Button>
          </div>
        )}

        {isError && !uncalibrated && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p role="alert" className="text-sm text-danger">
              {t(`${prefix}.loadFailed`)}
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        )}

        {data && <ProductInfoBody product={data} />}
      </DialogContent>
    </Dialog>
  );
}
